/**
 * What the editor is saved at, and how a save's answer or a re-read lands over
 * the work on screen. Pure: the sync context applies what these return.
 *
 * @module platform/editor-sync-baseline
 */

import type { Programme as BackendProgramme } from "@/server/types";
import { defaultProgramme } from "@/state/store";
import type { Programme } from "@/types";
import { stableJson } from "@/utils/stable-json";

type Structured = BackendProgramme["structured"];

/**
 * A document as the baseline holds it. `updatedAt` is left out: the session
 * stamps it on every send, so a copy differing only there has nothing to send.
 */
export function serializeForBaseline(programme: object): string {
  return JSON.stringify({ ...programme, updatedAt: undefined });
}

/** Equal by value. The stored copy orders keys differently from the editor. */
function same(a: unknown, b: unknown): boolean {
  return stableJson(a) === stableJson(b);
}

/** The first index of each id, as `findIndex` would answer it. */
function indexById(list: readonly { id: string }[]): Map<string, number> {
  const at = new Map<string, number>();
  list.forEach(({ id }, index) => {
    if (!at.has(id)) {
      at.set(id, index);
    }
  });
  return at;
}

export interface AdoptedComposition {
  /** The serialised document the editor is now saved at. */
  baseline: string;
  /** Stored modules to write into the buffer, by their index there. */
  modules: ReadonlyMap<number, Structured["modules"][number]>;
  /** The stored outcome map to write into the buffer, when it is taken. */
  ploToMimlos?: Structured["ploToMimlos"];
}

/**
 * What to take into the editor from a save composed from storage.
 *
 * A whole-document save keeps the stored copy of a module this editor did not
 * touch, and answers with it. Left unadopted, the screen shows a copy that is
 * not stored and calls it saved. An edit made since the save left is the
 * person's, so a module or map that moved on meanwhile is left alone.
 *
 * @param sent - The document the save carried.
 * @param saved - The document the save answered with.
 * @param current - The buffer now.
 */
export function adoptComposedModules(
  sent: Structured,
  saved: Structured,
  current: Programme,
): AdoptedComposition {
  const baseline = { ...sent, modules: [...(sent.modules ?? [])] };
  const modules = new Map<number, Structured["modules"][number]>();
  const sentAt = indexById(baseline.modules);
  const currentAt = indexById(current.modules ?? []);
  (saved.modules ?? []).forEach((module) => {
    const index = sentAt.get(module.id);
    const at = currentAt.get(module.id);
    if (index === undefined || at === undefined) {
      return;
    }
    const sentJson = stableJson(baseline.modules[index]);
    if (sentJson === stableJson(module) || stableJson(current.modules[at]) !== sentJson) {
      return;
    }
    modules.set(at, structuredClone(module));
    baseline.modules[index] = module;
  });
  const sentMap = sent.ploToMimlos ?? {};
  const adoptMap =
    !same(saved.ploToMimlos ?? {}, sentMap) && same(current.ploToMimlos ?? {}, sentMap);
  if (adoptMap) {
    baseline.ploToMimlos = saved.ploToMimlos ?? {};
  }
  return {
    baseline: serializeForBaseline(baseline),
    modules,
    ...(adoptMap ? { ploToMimlos: structuredClone(saved.ploToMimlos ?? {}) } : {}),
  };
}

/**
 * Fields a re-read does not rebase typed work over: a save merges the first
 * two by module base hash, and stamps the last on every write.
 */
const UNREBASED_FIELDS = new Set(["modules", "ploToMimlos", "updatedAt"]);

export interface RebasedWork {
  /** The baseline the typed work is now unsaved against. */
  baseline: string;
  /** Fields the reader left alone, at the read's values, to write into the buffer. */
  taken: Partial<Programme>;
}

/**
 * Carry what a re-read brought in under work typed while it was out. A field
 * the reader left alone takes the read's value, and one only the reader
 * changed keeps theirs, so saving the buffer cannot write a peer's field back.
 *
 * @param openBaseline - The document as the reader opened it.
 * @param read - The document the re-read returned, before the editor's defaults.
 * @param current - The buffer now, holding the typed work.
 * @returns Null, with nothing to take, when both changed one field differently.
 */
export function rebaseTypedWork(
  openBaseline: string,
  read: Structured,
  current: Programme,
): RebasedWork | null {
  const base = JSON.parse(openBaseline) as Record<string, unknown>;
  const mine = current as unknown as Readonly<Record<string, unknown>>;
  // Layered as the open copy was, so a field storage never held is not a move.
  const theirs = { ...defaultProgramme(), ...read } as unknown as Record<string, unknown>;
  const moved = [...new Set([...Object.keys(base), ...Object.keys(theirs)])].filter(
    (key) => !UNREBASED_FIELDS.has(key) && !same(theirs[key], base[key]),
  );
  const typed = (key: string) => !same(mine[key], base[key]);
  if (moved.some((key) => typed(key) && !same(mine[key], theirs[key]))) {
    return null;
  }
  const taken: Record<string, unknown> = {};
  moved.forEach((key) => {
    const value = theirs[key] === undefined ? undefined : structuredClone(theirs[key]);
    if (!typed(key)) {
      taken[key] = value;
    }
    base[key] = value;
  });
  return { baseline: serializeForBaseline(base), taken: taken as Partial<Programme> };
}
